/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Loading from '../../components/Loading';
import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../styles/layout/ActionBar';

// ─────────────────────────────────────────────────────────────────────────────
// Estilos locais
// ─────────────────────────────────────────────────────────────────────────────

const DashboardGrid = styled.div`
   display: grid;
   gap: 16px;
   grid-template-columns: 1fr;

   @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
   }

   @media (min-width: 1024px) {
      grid-template-columns: repeat(4, 1fr);
   }
`;

const KpiCard = styled.div`
   background: ${({ theme }) => theme?.colors?.surface ?? '#fff'};
   border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.08)'};
   border-radius: 12px;
   padding: 16px;
   box-shadow: ${({ theme }) => theme?.shadows?.sm ?? '0 1px 2px rgba(0,0,0,0.04)'};
`;

const KpiLabel = styled.div`
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.05em;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#888'};
   margin-bottom: 8px;
`;

const KpiValue = styled.div`
   font-size: 28px;
   font-weight: 700;
   color: ${({ $highlight, theme }) =>
      $highlight ? '#b71c1c' : (theme?.colors?.textPrimary ?? '#111')};
   line-height: 1;
`;

const KpiSubtext = styled.div`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#999'};
   margin-top: 6px;
`;

const TwoColumnGrid = styled.div`
   display: grid;
   gap: 16px;
   grid-template-columns: 1fr;

   @media (min-width: 900px) {
      grid-template-columns: 1fr 1fr;
   }
`;

const BarRow = styled.div`
   margin-bottom: 14px;

   &:last-child {
      margin-bottom: 0;
   }
`;

const BarLabel = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   font-size: 12px;
   margin-bottom: 5px;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#333'};
`;

const BarValue = styled.span`
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

const BarTrack = styled.div`
   background: ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.08)'};
   border-radius: 4px;
   height: 8px;
   overflow: hidden;
`;

const BarFill = styled.div`
   height: 100%;
   border-radius: 4px;
   width: ${({ $percent }) => $percent ?? 0}%;
   background: ${({ $color }) => $color ?? '#185fa5'};
   transition: width 0.4s ease;
`;

const TechnicianRow = styled.div`
   padding: 10px 0;
   border-bottom: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.06)'};
   cursor: pointer;

   &:last-child {
      border-bottom: none;
   }

   &:hover {
      opacity: 0.8;
   }
`;

const TechnicianHeader = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 5px;
`;

const TechnicianName = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

const TechnicianStats = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#888'};
`;

const OverdueRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 8px 10px;
   border-radius: 8px;
   background: ${({ theme }) => theme?.colors?.surface ?? '#fafafa'};
   border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.07)'};
   margin-bottom: 8px;
   cursor: pointer;
   font-size: 12px;

   &:hover {
      background: ${({ theme }) => theme?.colors?.primarySoft ?? 'rgba(24,95,165,0.05)'};
   }

   &:last-child {
      margin-bottom: 0;
   }
`;

const OverdueOsNumber = styled.span`
   font-weight: 700;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#111'};
`;

const OverdueEquipment = styled.span`
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#888'};
   flex: 1;
   text-align: center;
`;

const OverdueDays = styled.span`
   font-weight: 700;
   color: #b71c1c;
`;

const SlaNote = styled.p`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#aaa'};
   margin-top: 10px;
   margin-bottom: 0;
`;

const PeriodSelector = styled.div`
   display: flex;
   gap: 6px;
`;

const PeriodButton = styled.button`
   padding: 4px 12px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: ${({ $active }) => ($active ? '700' : '500')};
   border: 1px solid
      ${({ $active, theme }) =>
         $active
            ? (theme?.colors?.appBackground ?? '#1a237e')
            : (theme?.colors?.border ?? 'rgba(0,0,0,0.15)')};
   background: ${({ $active, theme }) =>
      $active ? (theme?.colors?.appBackground ?? '#1a237e') : 'transparent'};
   color: ${({ $active, theme }) =>
      $active ? '#fff' : (theme?.colors?.textMuted ?? '#555')};
   cursor: pointer;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Dados mock — serão substituídos por chamadas à API
// ─────────────────────────────────────────────────────────────────────────────

const MOCK_DASHBOARD_DATA = {
   kpis: {
      totalOpenedInPeriod:      47,
      totalCompletedInPeriod:   31,
      completionRatePercent:    66,
      averageTmrDays:           3.2,
      resolutionRatePercent:    89,
      overdueCount:             4,
   },
   statusDistribution: [
      { status_label: 'Em reparo',          count: 14, color: '#185fa5' },
      { status_label: 'Em triagem',         count: 7,  color: '#534ab7' },
      { status_label: 'Aguard. peças',      count: 6,  color: '#854f0b' },
      { status_label: 'Pronto p/ entrega',  count: 5,  color: '#3b6d11' },
      { status_label: 'Aguard. aprovação',  count: 3,  color: '#993c1d' },
      { status_label: 'Entregue/Devolvida', count: 12, color: '#5f5e5a' },
   ],
   technicianProductivity: [
      {
         technician_name:          'Lucas Teixeira',
         completed_os_count:       18,
         average_tmr_days:         2.8,
         resolution_rate_percent:  92,
         productivity_score:       90,
      },
      {
         technician_name:          'Ana Rodrigues',
         completed_os_count:       13,
         average_tmr_days:         3.5,
         resolution_rate_percent:  85,
         productivity_score:       65,
      },
      {
         technician_name:          'Marcos Vieira',
         completed_os_count:       9,
         average_tmr_days:         4.1,
         resolution_rate_percent:  78,
         productivity_score:       45,
      },
   ],
   overdueServiceOrders: [
      { id: 45, os_number: 'OS-2026-0045', equipment_description: 'Bebedouro Garrafão',  overdue_days: 7 },
      { id: 39, os_number: 'OS-2026-0039', equipment_description: 'Purificador Slim',    overdue_days: 4 },
      { id: 36, os_number: 'OS-2026-0036', equipment_description: 'Torneira 3T',          overdue_days: 3 },
      { id: 33, os_number: 'OS-2026-0033', equipment_description: 'Purificador Master',  overdue_days: 1 },
   ],
   commonDefectTypes: [
      { defect_label: 'Vazamento',           percentage: 34 },
      { defect_label: 'Não gela / não aquece',percentage: 28 },
      { defect_label: 'Troca de filtro',     percentage: 18 },
      { defect_label: 'Elétrico / não liga', percentage: 12 },
      { defect_label: 'Outros',              percentage: 8  },
   ],
};

const PERIOD_OPTIONS = [
   { value: 'week',  label: '7 dias' },
   { value: 'month', label: 'Mês atual' },
   { value: '3m',    label: '3 meses' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

export default function ServiceOrderDashboard() {
   const navigate = useNavigate();

   // eslint-disable-next-line no-unused-vars
   const [isLoading, setIsLoading] = useState(false);
   const [selectedPeriod, setSelectedPeriod] = useState('month');
   const [dashboardData] = useState(MOCK_DASHBOARD_DATA);

   const { kpis, statusDistribution, technicianProductivity, overdueServiceOrders, commonDefectTypes } =
      dashboardData;

   const statusDistributionMax = useMemo(
      () => Math.max(...statusDistribution.map((item) => item.count), 1),
      [statusDistribution]
   );

   const handleOverdueRowClick = (serviceOrderId) => {
      navigate(`/assistencia-tecnica/ordens-de-servico/${serviceOrderId}`);
   };

   const handleTechnicianClick = (technicianName) => {
      navigate(`/assistencia-tecnica/ordens-de-servico?technician=${encodeURIComponent(technicianName)}`);
   };

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Dashboard — Assistência Técnica</PageTitle>
               <PageSubtitle>
                  Indicadores de desempenho e SLA do departamento. Grupo MyShop Goiânia.
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         {/* ── Seletor de período ── */}
         <Section>
            <SectionBody>
               <FormGrid $cols={12}>
                  <Field className="col-4">
                     <label>Período de análise</label>
                     <PeriodSelector>
                        {PERIOD_OPTIONS.map((periodOption) => (
                           <PeriodButton
                              key={periodOption.value}
                              type="button"
                              $active={selectedPeriod === periodOption.value}
                              onClick={() => setSelectedPeriod(periodOption.value)}
                           >
                              {periodOption.label}
                           </PeriodButton>
                        ))}
                     </PeriodSelector>
                  </Field>

                  <Field className="col-3">
                     <label>Técnico</label>
                     <select>
                        <option value="">Todos os técnicos</option>
                        {technicianProductivity.map((technician) => (
                           <option key={technician.technician_name} value={technician.technician_name}>
                              {technician.technician_name}
                           </option>
                        ))}
                     </select>
                  </Field>

                  <Field className="col-5" $alignEnd>
                     <label>&nbsp;</label>
                     <ActionBar>
                        <ActionBarRight>
                           <button type="button" disabled={isLoading}>
                              Atualizar
                           </button>
                        </ActionBarRight>
                     </ActionBar>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* ── KPIs principais ── */}
         <DashboardGrid style={{ marginBottom: '16px' }}>
            <KpiCard>
               <KpiLabel>OSs abertas</KpiLabel>
               <KpiValue>{kpis.totalOpenedInPeriod}</KpiValue>
               <KpiSubtext>no período</KpiSubtext>
            </KpiCard>

            <KpiCard>
               <KpiLabel>Concluídas</KpiLabel>
               <KpiValue>{kpis.totalCompletedInPeriod}</KpiValue>
               <KpiSubtext>{kpis.completionRatePercent}% do total</KpiSubtext>
            </KpiCard>

            <KpiCard>
               <KpiLabel>TMR médio</KpiLabel>
               <KpiValue>{kpis.averageTmrDays.toFixed(1)}d</KpiValue>
               <KpiSubtext>tempo médio de reparo</KpiSubtext>
            </KpiCard>

            <KpiCard>
               <KpiLabel>Taxa de resolução</KpiLabel>
               <KpiValue $highlight={kpis.resolutionRatePercent < 70}>
                  {kpis.resolutionRatePercent}%
               </KpiValue>
               <KpiSubtext>reparos concluídos com sucesso</KpiSubtext>
            </KpiCard>
         </DashboardGrid>

         {/* ── Distribuição de status + Produtividade por técnico ── */}
         <TwoColumnGrid style={{ marginBottom: '16px' }}>

            {/* Distribuição de status */}
            <Section style={{ marginBottom: 0 }}>
               <SectionHeader>
                  <SectionTitle>Distribuição por status</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  {statusDistribution.map((statusItem) => (
                     <BarRow key={statusItem.status_label}>
                        <BarLabel>
                           <span>{statusItem.status_label}</span>
                           <BarValue>{statusItem.count}</BarValue>
                        </BarLabel>
                        <BarTrack>
                           <BarFill
                              $percent={(statusItem.count / statusDistributionMax) * 100}
                              $color={statusItem.color}
                           />
                        </BarTrack>
                     </BarRow>
                  ))}
               </SectionBody>
            </Section>

            {/* Produtividade por técnico */}
            <Section style={{ marginBottom: 0 }}>
               <SectionHeader>
                  <SectionTitle>Produtividade por técnico</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  {technicianProductivity.map((technicianItem) => (
                     <TechnicianRow
                        key={technicianItem.technician_name}
                        onClick={() => handleTechnicianClick(technicianItem.technician_name)}
                        title="Clique para filtrar OSs deste técnico"
                     >
                        <TechnicianHeader>
                           <TechnicianName>{technicianItem.technician_name}</TechnicianName>
                           <TechnicianStats>
                              {technicianItem.completed_os_count} OS ·{' '}
                              TMR {technicianItem.average_tmr_days.toFixed(1)}d ·{' '}
                              {technicianItem.resolution_rate_percent}% resolução
                           </TechnicianStats>
                        </TechnicianHeader>
                        <BarTrack>
                           <BarFill
                              $percent={technicianItem.productivity_score}
                              $color="#185fa5"
                           />
                        </BarTrack>
                     </TechnicianRow>
                  ))}
               </SectionBody>
            </Section>

         </TwoColumnGrid>

         {/* ── OSs em atraso + Tipos de defeito ── */}
         <TwoColumnGrid>

            {/* OSs em atraso */}
            <Section style={{ marginBottom: 0 }}>
               <SectionHeader>
                  <SectionTitle>
                     OSs em atraso{' '}
                     {kpis.overdueCount > 0 && (
                        <span
                           style={{
                              fontSize: '11px',
                              fontWeight: '700',
                              background: 'rgba(183,28,28,0.1)',
                              color: '#b71c1c',
                              border: '1px solid rgba(183,28,28,0.25)',
                              borderRadius: '999px',
                              padding: '2px 8px',
                              marginLeft: '8px',
                           }}
                        >
                           {kpis.overdueCount} OSs
                        </span>
                     )}
                  </SectionTitle>
               </SectionHeader>
               <SectionBody>
                  {overdueServiceOrders.length ? (
                     <>
                        {overdueServiceOrders.map((overdueItem) => (
                           <OverdueRow
                              key={overdueItem.id}
                              onClick={() => handleOverdueRowClick(overdueItem.id)}
                              title="Clique para abrir esta OS"
                           >
                              <OverdueOsNumber>{overdueItem.os_number}</OverdueOsNumber>
                              <OverdueEquipment>{overdueItem.equipment_description}</OverdueEquipment>
                              <OverdueDays>+{overdueItem.overdue_days}d atraso</OverdueDays>
                           </OverdueRow>
                        ))}
                        <SlaNote>SLA padrão: configurável por tipo de equipamento.</SlaNote>
                     </>
                  ) : (
                     <p style={{ fontSize: '13px', color: '#888' }}>
                        Nenhuma OS em atraso. Ótimo desempenho!
                     </p>
                  )}
               </SectionBody>
            </Section>

            {/* Tipos de defeito mais comuns */}
            <Section style={{ marginBottom: 0 }}>
               <SectionHeader>
                  <SectionTitle>Defeitos mais comuns</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  {commonDefectTypes.map((defectItem) => (
                     <BarRow key={defectItem.defect_label}>
                        <BarLabel>
                           <span>{defectItem.defect_label}</span>
                           <BarValue>{defectItem.percentage}%</BarValue>
                        </BarLabel>
                        <BarTrack>
                           <BarFill
                              $percent={defectItem.percentage}
                              $color="#1d9e75"
                           />
                        </BarTrack>
                     </BarRow>
                  ))}
               </SectionBody>
            </Section>

         </TwoColumnGrid>
      </Container>
   );
}
